import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink
} from "reactstrap"
import {
  getRFQsByStatus,
  exportRFQsToCSV,
  formatCurrency,
  getStatusColor
} from "../../../../helpers/rfq_dashboard_api"

const StatusCardModal = ({
  isOpen,
  toggle,
  cardType,
  filters = {}
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [entriesPerPage, setEntriesPerPage] = useState(10)

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && cardType) {
      setCurrentPage(1) // Reset to first page when modal opens
      loadData(1)
    }
  }, [isOpen, cardType, filters])

  // Reload data when perPage changes
  useEffect(() => {
    if (isOpen && cardType) {
      setCurrentPage(1) // Reset to first page
      loadData(1) // Load first page with new page size
    }
  }, [perPage])

  // Handle page change
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= (pagination?.total_pages || 1)) {
      setCurrentPage(page)
      loadData(page)
    }
  }

  const loadData = async (page = currentPage) => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading RFQs for card type:", cardType, "with filters:", filters, "page:", page)

      // Add pagination parameters to filters
      const paginatedFilters = {
        ...filters,
        page: page,
        per_page: perPage
      }

      const response = await getRFQsByStatus(cardType, paginatedFilters)
      console.log("RFQs response:", response)
      console.log("Response data structure:", response?.data)

      if (response && response.status) {
        // Extract the rfqs array and pagination from the nested data structure
        const rfqsData = response.data?.rfqs || []
        const paginationData = response.data?.pagination || null

        console.log("Extracted RFQs data:", rfqsData)
        console.log("Pagination data:", paginationData)
        console.log("RFQs data length:", rfqsData.length)

        setData(rfqsData)
        setPagination(paginationData)
        console.log("RFQs data set successfully:", rfqsData)
      } else {
        console.error("API response failed:", response)
        throw new Error(response?.message || "Failed to load RFQ data")
      }
    } catch (err) {
      console.error("Error loading RFQ data:", err)
      setError(err.message || "Failed to load RFQ data")
      setData([])
      setPagination(null)
    } finally {
      setLoading(false)
    }
  }

  const handleExportCSV = async () => {
    try {
      setExporting(true)

      const response = await exportRFQsToCSV(cardType, filters)

      if (response && response.status) {
        // Create and trigger download
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${getModalTitle()}_RFQs_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error(response?.message || "Failed to export data")
      }
    } catch (err) {
      console.error("Export error:", err)
      alert("Failed to export data: " + err.message)
    } finally {
      setExporting(false)
    }
  }

  const getModalTitle = () => {
    switch (cardType) {
      case 'total':
        return 'Total RFQs'
      case 'created':
        return 'Created RFQs'
      case 'wip':
        return 'Work in Progress RFQs'
      case 'closed':
        return 'Closed RFQs'
      default:
        return 'RFQs'
    }
  }

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'created':
        return 'success'
      case 'in progress':
      case 'work in progress':
        return 'warning'
      case 'completed':
      case 'closed':
        return 'info'
      case 'cancelled':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  // Filter data based on search term
  const filteredData = (Array.isArray(data) ? data : []).filter(item => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      item.rfq_number?.toLowerCase().includes(searchLower) ||
      item.title?.toLowerCase().includes(searchLower) ||
      item.project?.name?.toLowerCase().includes(searchLower) ||
      item.service_head_name?.toLowerCase().includes(searchLower) ||
      item.vendor?.name?.toLowerCase().includes(searchLower) ||
      item.current_status?.toLowerCase().includes(searchLower)
    )
  })

  // Debug logging
  console.log("StatusCardModal render - data:", data)
  console.log("StatusCardModal render - filteredData:", filteredData)
  console.log("StatusCardModal render - searchTerm:", searchTerm)



  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        <i className="ri-file-list-3-line me-2"></i>
        {getModalTitle()}
        <Badge color="primary" className="ms-2">
          {filteredData.length} records
        </Badge>
      </ModalHeader>
      <ModalBody>
        {/* Search and Export Controls */}
        <Row className="mb-3">
          <Col md={6}>
          <div className="search-box">
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder="Search by RFQ#, title, project, service head, vendor, or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Show:</Label>
              <Input
                type="select"
                value={entriesPerPage}
                onChange={(e) => {
                  const newEntriesPerPage = parseInt(e.target.value)
                  setEntriesPerPage(newEntriesPerPage)
                  setPerPage(newEntriesPerPage)
                  setCurrentPage(1) // Reset to first page
                }}
                style={{minWidth: '80px'}}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
          </Col>
          <Col md={2} className="text-end">
            <Button
              color="success"
              onClick={handleExportCSV}
              block
              disabled={exporting || !Array.isArray(data) || data.length === 0}
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bx bx-download me-1"></i>
                  Export CSV
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading RFQ data...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert color="danger">
            <h6>Error Loading Data</h6>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {/* Data Table */}
        {!loading && !error && (
          <>
            {filteredData.length > 0 ? (
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>RFQ#</th>
                      <th>RFQ Title</th>
                      <th>Project</th>
                      <th>Good & Service Head</th>
                      <th>Vendor</th>
                      <th>Budget</th>
                      <th>Final Amount</th>
                      <th>Created Date</th>
                      {/* Conditional columns based on card type */}
                      {cardType !== 'created' && <th>Estimated Date</th>}
                      {cardType !== 'created' && cardType !== 'wip' && <th>Completion Date</th>}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((rfq, index) => (
                      <tr key={rfq.rfq_id || index}>
                        <td>
                          <strong>{rfq.rfq_number || '-'}</strong>
                        </td>
                        <td>{rfq.title || '-'}</td>
                        <td>{rfq.project?.name || '-'}</td>
                        <td>{rfq.service_head_name || '-'}</td>
                        <td>{rfq.vendor?.name || 'Not Assigned'}</td>
                        <td>{formatCurrency(rfq.budget)}</td>
                        <td>{formatCurrency(rfq.final_amount)}</td>
                        <td>{formatDate(rfq.created_at)}</td>
                        {/* Conditional columns based on card type */}
                        {cardType !== 'created' && (
                          <td>
                            <span className={rfq.estimated_date ? 'text-info' : 'text-muted'}>
                              {rfq.estimated_date || '-'}
                            </span>
                          </td>
                        )}
                        {cardType !== 'created' && cardType !== 'wip' && (
                          <td>
                            <span className={rfq.completion_date ? 'text-success' : 'text-muted'}>
                              {formatDate(rfq.completion_date) || '-'}
                            </span>
                          </td>
                        )}
                        <td>
                          <Badge color={getStatusBadgeColor(rfq.current_status)}>
                            {rfq.current_status || '-'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  <i className="ri-file-list-3-line display-4 text-muted"></i>
                  <h5 className="mt-3">No RFQs Found</h5>
                  <p className="text-muted">
                    {searchTerm ? 'No RFQs match your search criteria.' : 'No RFQs available for this category.'}
                  </p>
                  {!searchTerm && (
                    <small className="text-muted">
                      Data count: {Array.isArray(data) ? data.length : 0} records loaded
                    </small>
                  )}
                </td>
              </tr>
            )}
          </>
        )}

        {/* Pagination Controls - Always show when there's data */}
        {!loading && !error && data && data.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3 px-3">
            <div className="text-muted">
              {pagination ? (
                <>
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                  {pagination.total} entries
                </>
              ) : (
                <>Showing {data.length} entries</>
              )}
            </div>

            {/* Only show pagination controls if there are multiple pages */}
            {pagination && pagination.total_pages > 1 && (
              <Pagination className="mb-0">
                <PaginationItem disabled={!pagination.has_prev}>
                  <PaginationLink
                    previous
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                  />
                </PaginationItem>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }

                  return (
                    <PaginationItem key={pageNum} active={pageNum === pagination.current_page}>
                      <PaginationLink onClick={() => handlePageChange(pageNum)}>
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem disabled={!pagination.has_next}>
                  <PaginationLink
                    next
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                  />
                </PaginationItem>
              </Pagination>
            )}
          </div>
        )}


      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default StatusCardModal
